/**
 * Report drawer — Sprint 5.
 *
 * Writes a Basic Text SR via the SR writer, then POSTs to viewer-api
 * which forwards to scp-daemon via C-STORE. The PDF signing step
 * (`/viewer-api/reports/{uid}/sign`) is stubbed with an /sr alias —
 * wiring to the real endpoint is a viewer-api side follow-up.
 */
import { createSignal, Show } from "solid-js";
import type { Measurement } from "./types";
import { writeBasicTextSR } from "./sr";
import { auth } from "../auth/store";

export interface ReportPanelProps {
  studyUid: string;
  mrn: string;
  patientName: string;
  measurements: Measurement[];
  open: boolean;
  onClose: () => void;
}

export function ReportPanel(props: ReportPanelProps) {
  const [text, setText] = createSignal("");
  const [busy, setBusy] = createSignal(false);
  const [result, setResult] = createSignal<"ok" | "fail" | null>(null);
  const [detail, setDetail] = createSignal<string>("");

  async function sign() {
    setBusy(true);
    setResult(null);
    try {
      const bytes = writeBasicTextSR({
        studyUid: props.studyUid,
        mrn: props.mrn,
        patientName: props.patientName,
        text: text(),
        measurements: props.measurements,
      });
      const tok = auth()?.accessToken;
      const rsp = await fetch(`/viewer-api/reports/${encodeURIComponent(props.studyUid)}/sr`, {
        method: "POST",
        headers: {
          "Content-Type": "application/dicom",
          ...(tok ? { Authorization: `Bearer ${tok}` } : {}),
        },
        body: new Uint8Array(bytes.buffer, bytes.byteOffset, bytes.byteLength),
      });
      if (!rsp.ok) {
        setResult("fail");
        setDetail(`HTTP ${rsp.status}: ${await rsp.text()}`);
      } else {
        setResult("ok");
        setDetail("");
      }
    } catch (err) {
      setResult("fail");
      setDetail(String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Show when={props.open}>
      <div class="fixed inset-0 z-40 bg-black/40" onClick={props.onClose} />
      <aside class="fixed inset-y-0 right-0 z-50 flex w-[460px] flex-col border-l border-ink-300 bg-ink-100 text-ink-900 shadow-panel">
        <header class="flex items-center justify-between border-b border-ink-300 px-4 py-2">
          <div>
            <div class="text-sm font-semibold text-white">Sign report</div>
            <div class="text-[11px] text-ink-700">Basic Text SR → C-STORE → RIS</div>
          </div>
          <button class="btn-ghost text-xs" onClick={props.onClose}>Close</button>
        </header>
        <div class="flex-1 overflow-auto p-4 text-sm">
          <label class="mb-1 block text-[11px] uppercase tracking-wider text-ink-700">
            Patient
          </label>
          <div class="mb-3 rounded border border-ink-300 bg-ink-200 px-2 py-1 text-[12px]">
            <span class="font-medium text-white">{props.patientName || "UNKNOWN"}</span>
            <span class="ml-2 text-ink-700">· MRN</span>
            <span class="ml-1 font-mono">{props.mrn || "—"}</span>
          </div>
          <label class="mb-1 block text-[11px] uppercase tracking-wider text-ink-700">
            Findings
          </label>
          <textarea
            class="mb-3 w-full rounded border border-ink-300 bg-ink-200 p-2 text-sm leading-5 outline-none focus:border-brand"
            rows="10"
            value={text()}
            onInput={(e) => setText(e.currentTarget.value)}
            placeholder="Clinical observations…"
          />
          <label class="mb-1 block text-[11px] uppercase tracking-wider text-ink-700">
            Measurements ({props.measurements.length})
          </label>
          <ul class="mb-3 space-y-1">
            {props.measurements.map((m) => (
              <li class="rounded border border-ink-300 bg-ink-200 px-2 py-1 text-xs">
                <span class="chip-brand mr-2">{m.kind}</span>
                <span class="font-mono">{m.derivedMm?.toFixed(1) ?? "—"} mm</span>
              </li>
            ))}
          </ul>
        </div>
        <footer class="flex items-center justify-end gap-2 border-t border-ink-300 p-3">
          <button class="btn-ghost" onClick={props.onClose}>Discard</button>
          <button class="btn-primary" disabled={busy()} onClick={sign}>
            {busy() ? "Signing…" : "Sign + send"}
          </button>
        </footer>
        <Show when={result()}>
          <div class="m-3 rounded border p-2 text-xs"
               classList={{
                 "border-brand/50 bg-brand/10 text-brand": result() === "ok",
                 "border-danger/50 bg-danger/10 text-danger": result() === "fail",
               }}>
            {result() === "ok" ? "Report sent" : `Failed — ${detail()}`}
          </div>
        </Show>
      </aside>
    </Show>
  );
}
